import { faInbox } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { message, Button, Upload as AntdUpload } from 'antd';
import type { RcFile, UploadFile, UploadProps as AntdUploadProps } from 'antd/lib/upload/interface';
import type React from 'react';
import { useState } from 'react';

import { getTranslatedString } from '@/utils';
import { showError } from '@/utils/common';

const { Dragger } = AntdUpload;

interface UploadProps extends AntdUploadProps {
  title?: string;
  description?: string;
  children?: React.ReactNode;
  request?: (fileList: RcFile[]) => Promise<any>;
  /** When true, do not show the generic \"Upload Successfully.\" after request resolves (caller shows its own feedback). */
  suppressBuiltInSuccess?: boolean;
  /** When true, do not call showError when request rejects (caller owns error feedback, e.g. a warning + action). */
  suppressBuiltInError?: boolean;
}

export const Upload = ({
  title = 'Click or drag file to this area to upload',
  description = 'Support for a single or bulk upload.',
  multiple = true,
  accept,
  disabled,
  request,
  suppressBuiltInSuccess = false,
  suppressBuiltInError = false,
}: UploadProps) => {
  const [uploading, setUploading] = useState<boolean>(false);
  const [fileList, setFileList] = useState<any[]>([]);

  const handleUpload = () => {
    if (!request) {
      return;
    }

    setUploading(true);

    return request(fileList)
      .then(() => {
        setUploading(false);
        setFileList([]);
        if (!suppressBuiltInSuccess) {
          message.success('Upload Successfully.');
        }
      })
      .catch((uploadErr) => {
        setUploading(false);
        if (!suppressBuiltInError) {
          showError(uploadErr);
        }
      });
  };

  const beforeUpload = (file: RcFile) => {
    // multiple={false} → newest pick replaces previous (sane single-file UX).
    // multiple={true}  → append, supporting bulk selection.
    setFileList((prev: RcFile[]) => (multiple ? [...prev, file] : [file]));
    return false;
  };

  const onRemove = (file: UploadFile) => {
    setFileList((prevFileList: RcFile[]) => prevFileList.filter(({ uid }: RcFile) => uid !== file.uid));
  };

  const uploadingLabel: string = getTranslatedString('attachments.uploading');
  const startUploadLabel: string = getTranslatedString('attachments.start-upload');

  return (
    <>
      <Dragger
        multiple={multiple}
        accept={accept}
        disabled={disabled}
        fileList={fileList}
        beforeUpload={beforeUpload}
        onRemove={onRemove}
      >
        <p className="ant-upload-drag-icon">
          <span className="anticon anticon-inbox">
            <FontAwesomeIcon icon={faInbox} />
          </span>
        </p>
        <p className="ant-upload-text">{title}</p>
        <p className="ant-upload-hint">{description}</p>
      </Dragger>

      <Button
        block={true}
        type="primary"
        onClick={handleUpload}
        disabled={fileList.length === 0 || disabled}
        loading={uploading}
        style={{ marginTop: 16 }}
      >
        {uploading ? uploadingLabel : startUploadLabel}
      </Button>
    </>
  );
};
